import React, { useCallback, useEffect, useMemo, useState, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/api";
import { SnackbarProvider, closeSnackbar, enqueueSnackbar } from "notistack";
import { Box, Button, CircularProgress } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { sub_query_system, sub_query_user, userTokenID } from "./query/queries";
import useAuth from "./useAuth";
import { appf_tenant_info, poolData } from "./UserPool";
import { AppContextProvider } from "./context/app/AppContext";
import CoreStoreProvider from "./store/CoreStoreProvider";
import { getCoreStore } from "./store/coreStore";
import { ConfirmationContextProvider } from "./context/confirmation-modal/ConfirmationModal";
import { ThemeContextProvider } from "./components/theme-toggler/theme-context/index";
import { UserContextProvider } from "./context/user/UserContext";
import Login from "./components/Login"; // keep eager for fastest login
import LoginAzure from "./components/LoginAzure"; // keep eager for fastest login
import SidebarNav from "./components/SidebarNav";  //remove lazy
import { capitalize, decamelize, infoOptions } from "./components/commons";
import CloseIcon from "./asset/misc/Close"; // small icon OK to keep
import DesignSystemToast from "./ui/toast/DesignSystemToast";

import CheckLogin from "./CheckLogin"; //remove lazy
import RequireAuth from "./RequireAuth"; //remove lazy
import SecurityBlast from "./components/SecurityBlast";
import isForgeEnabled from "./service/local-storage/forge";
import SiteMcp from "./site/webmcp";
import ButlerBoot from "./butler/ButlerBoot";

import "./App.css";

// Lazy imports (code-splitting) for all authenticated / secondary routes & features
const NewPass = React.lazy(() => import("./components/NewPass"));
const Dashboard2 = React.lazy(() => import("./components/dashboard2"));
const Dashboard3 = React.lazy(() => import("./components/dashboard3"));
const Orion = React.lazy(() => import("./orion"));
const DashboardController = React.lazy(() => import("./components/dashboard-controller/DashboardController"));
/* Orion's applications page is parked, not removed: /apps goes straight to the
   existing page. To put it back in play, route /apps through
   components/apps-controller/AppsController (Cmd/Ctrl + Shift + 1-2, newEra). */
const OrionApps = React.lazy(() => import("./orion/apps"));
const Details = React.lazy(() => import("./components/Details"));

const EventLog = React.lazy(() => import("./components/eventlog/EventLog"));
const Eula = React.lazy(() => import("./components/eula/eula"));

// Existing lazies
/* The multiverse was Introspection, an ECharts graph. The page is three.js
   now and lives in components/multiverse; the old one is kept on disk and
   simply has no route any more. */
const Multiverse = React.lazy(() => import("./components/multiverse/MultiversePage"));
const Containers = React.lazy(() => import("./components/Containers"));
const Application = React.lazy(() => import("./components/Application"));
const Graviton = React.lazy(() => import("./components/graviton/Graviton"));
const Discovery = React.lazy(() => import("./components/Discovery"));
const Target = React.lazy(() => import("./components/Target"));
const DeployPage = React.lazy(() => import("./components/DeployPage"));
const DesignerPageWrapper = (props) => <DeployPage {...props} designer={true} />;
const DeployPageWrapper = (props) => <DeployPage {...props} />;
const Scan = React.lazy(() => import("./components/Scan"));

const Traceability = React.lazy(() => import("./components/traceability"));
const Forge = React.lazy(() => import("./components/forge"));
const ForgeRunPage = React.lazy(() => import("./components/forge/components/ForgeRunPage"));
const ForgeDocPage = React.lazy(() => import("./components/forge/components/ForgeDocPage"));
const ForgeWikiPage = React.lazy(() => import("./components/forge/components/ForgeWikiPage"));
const SiteContentPage = React.lazy(() => import("./components/SiteContentPage"));
/* lazy for a reason: the model runtime it pulls in is fourteen megabytes, and
   it is only fetched for someone who opens this page */
const ButlerPage = React.lazy(() => import("./components/ButlerPage"));

// Added better Suspense fallback component
const SuspenseFallback = () => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: 2,
    py: 6,
    color: 'rgba(255,255,255,0.7)',
    fontSize: '12px',
    letterSpacing: '0.5px'
  }}>
    <CircularProgress size={42} thickness={4} />
  </Box>
);


const snackbarComponents = {
  success: DesignSystemToast,
  error: DesignSystemToast,
  info: DesignSystemToast
};

export const tableBGColor = "rgba(23, 23, 23, 1)";

if (typeof localStorage.appf_tenant_info === 'undefined') {
  setTimeout(() => {
    console.log("[2] - Reloading ... ");
  }, 2100);
}

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    info: {
      main: 'rgb(0,0,0)'
    },
    background: {
      default: tableBGColor
    }
  },
  typography: {
    fontFamily: ['Inter'],
    button: {
      textTransform: 'none',
      fontSize: '12px',
      fontWeight: 'normal !important'
    }
  },
  components: {
    MuiTreeItem: {
      styleOverrides: {
        root: {
          fontSize: '12px',
         "& > .MuiTreeItem-content": {
          border: "0.42px solid rgba(0, 0, 0,0)",
          "&:hover": {
            backgroundColor: "rgba(111, 111, 111, 0.04)",
          },
          "&.Mui-selected, &[data-focused]":{
            backgroundColor: "transparent",
          },
          "&.Mui-selected, &[data-selected]":{
            backgroundColor: "transparent !important",
          },
          "&:hover": {
            color:"rgba(246, 64, 13,0.82)"
          },
          "&.Mui-selected, &[data-selected]:before": {// .MuiTreeItem-label
            content: "' '",
            position: "absolute",
            top: "0px",
            left:"30px",
            width: "calc(100% - 34px)",
            height: "100%",
            borderRadius: "4px",
            backgroundColor: "rgba(246, 64, 13, 0.1) !important",
            border: "0.42px solid rgba(246, 64, 13,0.42)",
            color: "rgb(246, 64, 13)",
          },
          // Selected + Focused state
          "&.Mui-selected.Mui-focused": {
            backgroundColor: "rgba(0, 0, 0, 0.12)",
          },
        }
      },
        label: {
          fontSize: '12px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: "unset !important",
          borderRadius: "8px !important",
          backgroundColor: "rgba(32, 32, 32, 1) !important"
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "unset",
          border: "unset"
        }
      }
    },
    MuiMenu: {
      styleOverrides: {}
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          outline: "0.42px solid rgba(255,255,255,0.3)",
          border: "0.42px solid rgba(255,255,255,0.4)",
          borderRadius: "4px",
          backgroundColor: "rgba(26, 26, 26, 1)"
        },
        arrow: {
          color: "rgba(255,255,255,0.4)"
        }
      }
    },
    MuiTableFooter: {
      styleOverrides: {
        border: "none !important",
        root: {},
        "& :nth-of-type(odd)": {}
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {},
        "& :nth-of-type(odd)": {}
      }
    },
    MuiTable: {
      styleOverrides: {
        "root": {
          "& tr:nth-of-type(even)": {}
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {}
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          background: "transparent !important"
        },
        root: {}
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          font: "12px Inter",
          border: "none",
          borderBottom: "1px solid rgba(51,51,51,1)",
          background: "transparent"
        },
        head: {
          textTransform: "capitalize",
          height: 26,
          borderBottom: "1px solid rgba(0,0,0,0)",
          background: "transparent !important"
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        root: {
          color: "var(--scan-add-credential-input-text-color)"
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: "var(--scan-add-credential-input-text-color)"
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "var(--scan-add-credential-input-text-color)"
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "white",
          border: "none !important",
          '&.Mui-selected ': {
            background: `var(--color-main-bg-light)`,
            color: "var(--color-main-text)",
            font: "900 18px InterBlack",
            border: "unset !important"
          }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        MuiAlert: {
          message: {
            color: "red !important"
          }
        },
        root: {
          background: `transparent !important`,
          border: "none !important",
          borderRadius: "0 0 12px 12px"
        },
        gutters: {
          ":last-of-type": {}
        }
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          background: `rgba(222,222,222,0.1)`,
          border: "none",
          "&:hover": {
            background: `rgba(222,222,222,1)`,
            color: `rgba(0,0,0,1) !important`
          },
          "&:active": {
            background: `rgba(222,222,222,1)`,
            color: `rgba(0,0,0,1) !important`
          },
          "&:focus": {
            background: `rgba(222,222,222,1)`,
            color: `rgba(0,0,0,1) !important`
          }
        }
      }
    }
  }
});

const EmptyScreen = ()=> (<><Scan /></>);

export default function App(props) {
  const [appf_tenant_info] = useState(localStorage.appf_tenant_info?JSON.parse(localStorage.appf_tenant_info):{});
  const deployEnabled = typeof appf_tenant_info.sku!=="undefined"?( appf_tenant_info?.sku == "full-internal" || appf_tenant_info?.sku?.toLowerCase().indexOf("deploy") > -1 ) :false

  const { accessToken, authed } = useAuth();
  const navigate = useNavigate();

  const [notificationsEnabled] = useState(true);
  const location = useLocation();
  const [resourceConfig, setResourceConfig] = useState(null);

  // REACTIVE EULA STATE (was const eulaAccepted = localStorage.eula === "accepted")
  const [eulaAccepted, setEulaAccepted] = useState(localStorage.eula === "accepted");
  useEffect(() => {
    const eulaHandler = () => setEulaAccepted(localStorage.eula === "accepted");
    window.addEventListener('storage', eulaHandler);
    window.addEventListener('eulaAccepted', eulaHandler);
    return () => {
      window.removeEventListener('storage', eulaHandler);
      window.removeEventListener('eulaAccepted', eulaHandler);
    };
  }, []);

  const processNotification = (data) => {
    console.log(data)
    try {
      let notifType = "";
      let nd = "";
      const notification = JSON.parse(data.subscribe.data).notification_type;

      try {
        notifType = capitalize(decamelize(Object.keys(notification)[0]));
      } catch (e) { /* */ }

      try {
        nd = notification[Object.keys(notification)[0]];
      } catch (e) { /* */ }

      // Special handling for DeploymentProgress
      if (notifType === 'Deployment progress' && nd) {
        const { deployment_id, message, status } = nd;
        const variant = status === 'Failed' ? 'error' : status === 'Completed' ? 'success' : 'info';

        enqueueSnackbar(
          <div>
            <strong>{notifType}</strong>
            <br />
            {message || `Deployment ${deployment_id} - ${status}`}
          </div>,
          {
            variant,
            autoHideDuration: variant === 'error' ? 15000 : 10000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right'
            },
            action: (key) => (
              <Button onClick={() => closeSnackbar(key)}>
                <CloseIcon />
              </Button>
            )
          }
        );
        return;
      }

      // Special handling for DeploymentRollbackProgress
      if (notifType === 'Deployment rollback progress' && nd) {
        const { deployment_id, message, status } = nd;

        window.dispatchEvent(new CustomEvent('rollbackNotification', {
          detail: {
            message: message || `Rollback for ${deployment_id} - ${status}`,
            status
          }
        }));

        return;
      }

      let ndAction = "";

      try {
        ndAction = decamelize(capitalize(Object.keys(nd)[0]));
      } catch (e) { /* */ }

      let notifText = '';
      try {
        notifText = JSON.stringify(notification, null, 2);
      } catch (e) { /* */ }

      let id = "";
      try {
        id = nd[Object.keys(nd)[0]];
        // The application's name; the store first, then what Assess recorded
        // for components the store has not loaded, then the bare id.
        notifText = getCoreStore().applications.find(f => f.id == id)?.name
          ?? window._g?.assessData?.find(f => f.id == id)?.name
          ?? `${id}`;
      } catch (e) { /* */ }

      // "<Thing> progress" notices carry the stage as their key (Started,
      // Completed, Failed). They used to read as shouted machine output -
      // 'Assessment progress STARTED for "APP"' - and "started" sat beside the
      // UI's own "Assessment started" toast from the click, as if it were a
      // duplicate. This notice is the backend picking the work up after the
      // source was extracted, so "started" is said as "running", and the stage
      // sets the colour.
      const stage = `${ndAction}`.trim().toLowerCase();
      const progress = /^(.*) progress$/i.exec(notifType);
      if (progress && stage) {
        const what = progress[1];
        const said = { started: "running", completed: "completed", failed: "failed" }[stage] ?? stage;
        const variant = stage === "failed" ? "error" : stage === "completed" ? "success" : "info";
        enqueueSnackbar(
          <div>{`${what} ${said} for "${notifText}"`}</div>,
          {
            ...infoOptions,
            variant,
            action: (key) => (
              <Button onClick={() => closeSnackbar(key)}>
                <CloseIcon />
              </Button>
            )
          });
        return;
      }

      enqueueSnackbar(
        <div>
          {`${notifType} ${ndAction} for "${notifText}"`}
        </div>,
        {
          ...infoOptions,
          action: (key) => (
            <Button onClick={() => closeSnackbar(key)}>
              <CloseIcon />
            </Button>
          )
        })
    } catch (e) { 
      console.log(e)
     }
  }

  const notifySystemEvent = useCallback(({ data }) => {
    if (data) {
      // console.log("Notify system event", data,rest);
      processNotification(data);
    }
  }, []);

  const notifyUserEvent = notifySystemEvent(({ data }) => {
    if (data) {
      // console.log("Notify user event", data,rest);
      processNotification(data);
    }
  }, []);

  useEffect(() => {
    if (resourceConfig) {
      let client = generateClient();

      client
        .graphql({
          query: sub_query_system
        })
        .subscribe({
          next: notifySystemEvent,
          error: (error) => console.warn(error)
        });

      client
        .graphql({
          query: sub_query_user.replaceAll(userTokenID, poolData.session.accessToken.payload.username)
        })
        .subscribe({
          next: notifyUserEvent,
          error: (error) => console.warn(error)
        });
    }
  }, [notifySystemEvent, notifyUserEvent, resourceConfig]);

  useEffect(() => {
    if (notificationsEnabled && authed && poolData.accessToken != "" && appf_tenant_info.type === "cognito") {
      window._g.accT = poolData.accessToken;

      // Use relative endpoint locally so Vite proxy can forward WS to notifications.dev.appfactor.cloud
      const isLocalHost = (() => {
        try {
          const h = window.location.hostname;
          return h === 'localhost' || h === '127.0.0.1';
        } catch (_) { return false; }
      })();
      const gqlEndpoint = isLocalHost
        ? 'https://notifications.dev.appfactor.cloud/graphql'
        : poolData.envURL.replace("://", "://notifications.") + "/graphql";

      Amplify.configure({
        API: {
          GraphQL: {
            endpoint: gqlEndpoint,
            region: poolData.UserPoolId.split("_")[0],
            defaultAuthMode: "userPool"
          }
        }
      }, {
        Auth: {
          tokenProvider: {
            async getTokens() {
              return {
                accessToken: window._g.accT
              }
            }
          }
        }
      }
      );

      setTimeout(() => {
        setResourceConfig({});
      }, 420);
    }
  }, [accessToken, authed, notificationsEnabled]);

  useEffect(() => {
    if (localStorage.route) {
      let aRoute = localStorage.route;
      delete localStorage.route;

      navigate(aRoute, { replace: true });
    }
  }, [navigate]);


  const sideNavBarComp = useMemo(() => {
    let sideNavBar = (<></>);

    if (authed && eulaAccepted) {
      sideNavBar = <SidebarNav />;
    }

    return sideNavBar;
  }, [authed, eulaAccepted]);

  return (
    <ThemeProvider theme={darkTheme}>
      <ThemeContextProvider>
        <ConfirmationContextProvider>
          <SnackbarProvider
            hideIconVariant={true}
            Components={snackbarComponents}
            maxSnack={8}
          >
            {/* orion-tokens: the design system's palette, declared once for the
                whole app so the pages that wear its header band can read it.
                Variables only - `.orion` (the shell and its box-sizing reset)
                stays on the dashboard. */}
            <div className="app orion-tokens">
              <div className="app-container">
                {/* <div className={"grp" + `${window.location.pathname == "/login" ? "-" : ""}`}>
                  <div className="bg1" />
                </div> */}

                <UserContextProvider>
                  <div className="presentation"
                  style={
                    (location.pathname == "/wizzard") ? {
                     opacity: "0",
                     width: "0px",
                     visibility: "hidden",
                    } : {
                      visibility: "visible",
                    }
                  }
                  >
                    {sideNavBarComp}
                  </div>

                  <div className="app-frame">
                    {/* What this product is made of, said once and said
                        everywhere: the catalogue on `window.appfactor`, in the
                        document, and - where the browser has one - as tools an
                        agent can call to go anywhere in it. Here rather than on
                        the page that draws the map, because "ask anywhere to go
                        anywhere" is only true if it is on every page. */}
                    <SiteMcp forge={isForgeEnabled()} deploy={deployEnabled} />

                    {/* ...and the butler, if it is already on this machine: the
                        thing that has to understand an ask should be awake
                        before one arrives. It never downloads by itself. */}
                    <ButlerBoot />

                    {(eulaAccepted || (!authed)) ? (
                      <CoreStoreProvider assessmentClient={props.clientAssessment}>
                      <Suspense fallback={<SuspenseFallback />}>
                        <Routes>
                          <Route path="/apps" element={<RequireAuth><Application clientAssessment={props.clientAssessment} /></RequireAuth>} />
                          <Route path="/apps2" element={<RequireAuth><OrionApps clientAssessment={props.clientAssessment} /></RequireAuth>} />
                          <Route path="/assess" element={<RequireAuth><Application clientAssessment={props.clientAssessment} assess={true} /></RequireAuth>} />
                          <Route path="/graviton" element={<RequireAuth><Graviton clientAssessment={props.clientAssessment} assess={true} /></RequireAuth>} />
                          <Route path="/dotnetmodernization" element={<RequireAuth><Graviton isDotNetModernization={true} clientAssessment={props.clientAssessment} assess={true} /></RequireAuth>} />
                          <Route path="/discovery" element={<RequireAuth><Discovery viewOption="host" /></RequireAuth>} />
                          <Route path="/discoveryhost" element={<RequireAuth><Discovery viewOption="host" /></RequireAuth>} />
                          <Route path="/discoverysystem" element={<RequireAuth><Discovery viewOption="system" /></RequireAuth>} />
                          <Route path="/discoveryinstalled" element={<RequireAuth><Discovery viewOption="installed" /></RequireAuth>} />
                          <Route path="/discovery/:vo/:machineId" element={<RequireAuth><Details /></RequireAuth>} />
                          <Route path="/target" element={<RequireAuth><Target /></RequireAuth>} />
                          <Route path="/factor" element={<RequireAuth><Containers /></RequireAuth>} />
                         
                          <Route path="/wizzard" element={<RequireAuth><EmptyScreen /></RequireAuth>} />

                          {deployEnabled && <>
                          <Route path="/designer"     element={<RequireAuth><DesignerPageWrapper /></RequireAuth>} />
                            <Route path="/designer/:id" element={<RequireAuth><DesignerPageWrapper /></RequireAuth>} />
                            <Route path="/deploy"       element={<RequireAuth><DeployPageWrapper /></RequireAuth>} />
                            <Route path="/deploy/:id"   element={<RequireAuth><DeployPageWrapper /></RequireAuth>} />
                            </>
                          }

                          <Route path="/details" element={<RequireAuth><Details /></RequireAuth>} />
                          <Route path="/" element={appf_tenant_info.type == "azure" ? (authed ? <Navigate to="/dashboard" replace state={{ path: location.pathname }} /> : (props.activeAccount ? (<RequireAuth><Navigate to="/dashboard" replace state={{ path: location.pathname }} /></RequireAuth>) : <LoginAzure hash={location.hash} instance={props.instance} />)) : (<RequireAuth><Navigate to="/dashboard" replace state={{ path: location.pathname }} /></RequireAuth>)} />
                          <Route path="/dashboard" element={<RequireAuth><AppContextProvider clientAssessment={props.clientAssessment}><DashboardController assessment={props.assessment} clientAssessment={props.clientAssessment} /></AppContextProvider></RequireAuth>} />
                          <Route path="/dashboard2" element={<RequireAuth><AppContextProvider clientAssessment={props.clientAssessment}><Dashboard2 /></AppContextProvider></RequireAuth>} />
                          <Route path="/dashboard3" element={<RequireAuth><AppContextProvider clientAssessment={props.clientAssessment}><Dashboard3 /></AppContextProvider></RequireAuth>} />
                          <Route path="/dashboard4" element={<RequireAuth><AppContextProvider clientAssessment={props.clientAssessment}><Orion /></AppContextProvider></RequireAuth>} />

                          <Route path="/traceability" element={<RequireAuth><Traceability /></RequireAuth>} />
                          <Route path="/traceability/artifact-repositories" element={<RequireAuth><Traceability /></RequireAuth>} />
                          <Route path="/traceability/cmdb-records" element={<RequireAuth><Traceability /></RequireAuth>} />
                          <Route path="/forge" element={<RequireAuth>{isForgeEnabled() ? <Forge /> : <Navigate to="/dashboard" replace />}</RequireAuth>} />
                          <Route path="/forge/runs/:id" element={<RequireAuth>{isForgeEnabled() ? <ForgeRunPage /> : <Navigate to="/dashboard" replace />}</RequireAuth>} />
                            <Route path="/forge/doc/:id" element={<RequireAuth>{isForgeEnabled() ? <ForgeDocPage /> : <Navigate to="/dashboard" replace />}</RequireAuth>} />
                          <Route path="/forge/wiki" element={<RequireAuth>{isForgeEnabled() ? <ForgeWikiPage /> : <Navigate to="/dashboard" replace />}</RequireAuth>} />
                          <Route path="/multiverse" element={<RequireAuth><Multiverse clientAssessment={props.clientAssessment} /></RequireAuth>} />
                          <Route path="/security" element={<RequireAuth><SecurityBlast clientAssessment={props.clientAssessment} /></RequireAuth>} />
                          <Route path="/eventlog" element={<RequireAuth><EventLog /></RequireAuth>} />
                          <Route path="/w" element={<EmptyScreen />} />
                          
                          <Route path="/scan" element={<RequireAuth><Scan /></RequireAuth>} />

                          {/* The map of all of the above. */}
                          <Route path="/content" element={<RequireAuth><SiteContentPage /></RequireAuth>} />

                          {/* ...and something to ask about it, on this machine. */}
                          <Route path="/butler" element={<RequireAuth><ButlerPage /></RequireAuth>} />

                          {appf_tenant_info.type === "azure" ? (
                            <Route
                              path="/login"
                              element={props.instance && props.instance?.getActiveAccount() != null ? (
                                <Navigate
                                  to="/dashboard"
                                  replace
                                  state={{ path: location.pathname }}
                                />
                              ) : (
                                <LoginAzure
                                  hash={location.hash}
                                  instance={props.instance}
                                />
                              )}
                            />
                          ) : (
                            <Route path="/login" element={<Login />} />
                          )}

                          <Route path="/newpass" element={<NewPass />} />

                          <Route path="/checkLogin" element={<CheckLogin />} />
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Suspense>
                      </CoreStoreProvider>
                    ) : (
                      <Suspense fallback={<SuspenseFallback />}><Eula /></Suspense>
                    )}
                  </div>
                </UserContextProvider>
              </div>
            </div>
          </SnackbarProvider>
        </ConfirmationContextProvider>
      </ThemeContextProvider>
    </ThemeProvider>
  );
}
